<template>
  <div class="relative flex w-full items-center justify-between">
    <div class="flex w-full flex-shrink-0 items-center divide-x divide-gray-700">
      <DMenuBar
        :model="isDesignerPage ? menuItems : []"
        class="z-40 mx-auto h-14 w-full rounded-none border-x-0 border-t-0 border-b border-gray-800 bg-gray-900 px-4 text-base font-medium tracking-wide sm:px-4 md:px-6 md:font-normal"
        height="h-10">
        <template #start>
          <DisguiseLogo class="ml-4 h-auto cursor-pointer pl-4" @click="router.push('/')" />
        </template>
        <template #end>
          <div class="flex w-full justify-between">
            <Loader />
            <div class="flex items-center divide-x divide-gray-700">
              <div class="pr-5" v-if="!isDesignerPage">
                <DButton
                  severity="primary"
                  label=" New"
                  icon="pi pi-plus"
                  :disabled="!workspaceStore?.workspaces?.length"
                  v-tooltip="'Create a new file in the current workspace'"
                  @click="router.push('/new')">
                </DButton>
              </div>

              <div class="px-5">
                <OnlineAvatarGroup @logout="logout" />
              </div>
            </div>
          </div>
        </template>
      </DMenuBar>
    </div>
    <NewProjectDialog v-if="showNewProjectDialog" @close="showNewProjectDialog = !showNewProjectDialog" />
  </div>
</template>

<script setup>
import { DisguiseLogo, OnlineAvatarGroup } from '@/components';
import { useUserStore } from '@/stores/userStore';
import { DButton, DMenuBar } from '@disguise/cloud-ui';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import NewProjectDialog from '@/components/NewProjectDialog.vue';
import { useComments } from '@/composables/useComments';
import { useWorkspaceStore } from '@/stores/api/workspace';
import { useRouter } from 'vue-router';

import { useAppAction } from '@/composables/useAppAction';
import Loader from '../misc/Loader.vue';

const showNewProjectDialog = ref(false);
const route = useRoute();
const workspaceStore = useWorkspaceStore();
const router = useRouter();

// Check if we're on the designer page
const isDesignerPage = computed(() => {
  return route.path.startsWith('/editor/');
});

const {
  exit,
  newFile,
  addSceneNode,
  openLibraryModal,
  addModelSceneNode,
  addCameraSceneNode,
  newMeasurement,
  exportProjectorCsv,
  togglePhotometricView,
  exportScene,
} = useAppAction();
const { openNewCommentDialog } = useComments();

const menuItems = ref([
  {
    label: 'File',
    items: [
      { label: 'New', command: () => newFile() },
      {
        label: 'Export 3D Mesh',
        command: () => exportScene(),
      },
      {
        label: 'Export Projectors Data - CSV',
        command: () => exportProjectorCsv(),
      },

      { label: 'Exit', command: () => exit() },
    ],
  },
  {
    label: 'Primitives',
    items: [
      {
        label: 'Plane',

        command: () => {
          addSceneNode('plane');
        },
      },
      {
        label: 'Box',
        command: () => {
          addSceneNode('box');
        },
      },
      {
        label: 'Cylinder',
        command: () => {
          addSceneNode('cylinder');
        },
      },
      {
        label: 'Sphere',
        command: () => {
          addSceneNode('sphere');
        },
      },
      {
        label: 'Torus Knot',
        command: () => {
          addSceneNode('torusknot');
        },
      },

      {
        label: '3D Model',
        command: () => {
          addModelSceneNode();
        },
      },
    ],
  },
  {
    label: 'Projectors',
    items: [
      {
        label: 'LED Screen',
        command: () => {
          openLibraryModal('led');
        },
      },
      {
        label: 'Projector',
        command: () => {
          openLibraryModal('projector');
        },
      },

      {
        label: 'Camera Bookmark',
        command: () => {
          addCameraSceneNode();
        },
      },
    ],
  },
  {
    label: 'Array/Sym',
    items: [
      {
        label: 'Group & Arrays',
        command: () => {
          addSceneNode('group');
        },
      },
    ],
  },
  {
    label: 'Tools',
    items: [
      {
        label: 'Annotation',

        command: () => {
          openNewCommentDialog();
        },
      },
      {
        label: 'Measurement',

        command: () => {
          newMeasurement();
        },
      },
    ],
  },
  {
    label: 'Viewport',
    items: [
      {
        label: 'Photometric Analysis',
        command: () => {
          togglePhotometricView();
        },
      },
    ],
  },
  {
    label: 'Help',
    items: [
      {
        label: 'Tutorials',
        command: () => {
          window.open('https://help.disguise.one/cloud/mapping-matter/introduction', '_blank');
        },
      },
    ],
  },
]);

const logout = async () => {
  const userStore = useUserStore();

  try {
    // Perform logout directly
    await userStore.logoutUser();
  } catch (error) {
    console.error('Logout error:', error);
    // On error, redirect to home page
    router.push('/');
  }
};
</script>
